import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { orgsApi, type OrgRow } from '../features/orgs/api.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDate } from '../lib/dvDate.ts';
import { Pagination } from '../components/Pagination.tsx';
import { Icon } from '../components/Icon.tsx';

const PAGE_SIZE = 20;

export function AdminOrgs(): JSX.Element {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'orgs', page],
    queryFn: () => orgsApi.list(page, PAGE_SIZE),
  });
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const create = useMutation({
    mutationFn: () => orgsApi.create(name, contactEmail),
    onSuccess: async () => {
      setName('');
      setContactEmail('');
      await qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });

  return (
    <div className="page space-y-6 lg:space-y-8">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">{dv.orgs.title}</h1>
      </header>

      <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-3">
        <h2 className="status-label text-muted-foreground">{dv.orgs.create}</h2>
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && contactEmail.trim()) create.mutate();
          }}
        >
          <input
            className="input flex-1"
            placeholder={dv.orgs.name}
            aria-label={dv.orgs.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            className="input flex-1"
            type="email"
            dir="ltr"
            placeholder={dv.orgs.contactEmail}
            aria-label={dv.orgs.contactEmail}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={create.isPending}
            className="btn-primary"
          >
            {dv.orgs.create}
          </button>
        </form>
      </section>

      {isLoading && (
        <p role="status" aria-live="polite" className="status-label text-muted-foreground">
          {dv.common.loading}
        </p>
      )}

      {data && data.orgs.length === 0 && (
        <div className="rounded-3xl bg-[hsl(var(--chrome))] p-10 text-center space-y-3">
          <span className="mx-auto grid size-12 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
            <Icon name="apartment" size={22} />
          </span>
          <p className="text-xl text-ink">{dv.orgs.empty}</p>
        </div>
      )}

      {data && data.orgs.length > 0 && (
        <ul className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {data.orgs.map((o: OrgRow) => (
            <li key={o.id}>
              <Link
                to="/admin/orgs/$orgId"
                params={{ orgId: o.id }}
                className="flex items-center gap-4 px-4 sm:px-5 py-3.5 transition-colors hover:bg-[hsl(var(--surface-high))]"
              >
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                  <Icon name="apartment" size={22} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{o.name}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground truncate" dir="ltr">{o.contactEmail}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    <span className="tabular" dir="ltr">{o._count.users}</span> {dv.orgs.users}
                    <span className="hidden sm:inline"> · {formatDvDate(o.createdAt)}</span>
                  </p>
                </div>
                <span
                  className={`pill shrink-0 ${o.status === 'ACTIVE' ? 'pill-active' : 'pill-pending'}`}
                >
                  {o.status === 'ACTIVE' ? dv.common.active : dv.common.suspended}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Pagination
        page={data?.page ?? 1}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        total={data?.total ?? 0}
        onChange={setPage}
      />
    </div>
  );
}
